import { createContext, useContext, useEffect, useMemo, useState } from "react";

import {
  ArrowMark,
  BeaconMark,
  CauceMark,
  CimaMark,
  CrestMark,
  HelmMark,
  KeelMark,
  LlumMark,
  MeridianMark,
  NordMark,
  NorteMark,
  VantageMark,
} from "./logos";

/**
 * Las doce marcas candidatas para el despacho.
 *
 * `tagline` es la línea bajo el logo; `claim` es la frase larga que va en el
 * hero. Cada una se escribió desde el significado del nombre, no como relleno.
 */
export const BRANDS = [
  {
    id: "cauce",
    domain: "caucelegal.mx",
    email: "guillermo@caucelegal.mx",
    name: "Cauce",
    suffix: "Legal",
    Mark: CauceMark,
    tagline: "Abogados",
    claimWord: "encauzada",
    idea: "El cauce que ordena la corriente: dar dirección a un conflicto disperso.",
  },
  {
    id: "llum",
    domain: "llumlegal.mx",
    email: "guillermo@llumlegal.mx",
    name: "Llum",
    suffix: "Legal",
    Mark: LlumMark,
    tagline: "Abogados",
    claimWord: "esclarecida",
    idea: "Llum es luz en catalán, y es la torre donde ya está el despacho.",
  },
  {
    id: "cima",
    domain: "cimalegal.mx",
    email: "guillermo@cimalegal.mx",
    name: "Cima",
    suffix: "Legal",
    Mark: CimaMark,
    tagline: "Abogados",
    claimWord: "elevada",
    idea: "La cumbre: llegar arriba y ver el terreno completo antes de decidir.",
  },
  {
    id: "norte",
    domain: "nortelegal.mx",
    email: "guillermo@nortelegal.mx",
    name: "Norte",
    suffix: "Legal",
    Mark: NorteMark,
    tagline: "Abogados",
    claimWord: "orientada",
    idea: "La brújula: saber siempre hacia dónde va el caso.",
  },
  {
    id: "nord",
    domain: "nordlegal.mx",
    email: "guillermo@nordlegal.mx",
    name: "Nord",
    suffix: "Legal",
    Mark: NordMark,
    tagline: "Abogados",
    claimWord: "definida",
    idea: "La estrella polar: el punto del cielo que no se mueve.",
  },
  {
    id: "arrow",
    domain: "arrowlegal.mx",
    email: "guillermo@arrowlegal.mx",
    name: "Arrow",
    suffix: "Legal",
    Mark: ArrowMark,
    tagline: "Abogados",
    claimWord: "certera",
    idea: "La flecha: una sola dirección, sin desviarse en el camino.",
  },
  {
    id: "beacon",
    domain: "beaconlegal.mx",
    email: "guillermo@beaconlegal.mx",
    name: "Beacon",
    suffix: "Legal",
    Mark: BeaconMark,
    tagline: "Abogados",
    claimWord: "iluminada",
    idea: "Beacon es faro en inglés: la señal que se ve desde lejos.",
  },
  {
    id: "keel",
    domain: "keellegal.mx",
    email: "guillermo@keellegal.mx",
    name: "Keel",
    suffix: "Legal",
    Mark: KeelMark,
    tagline: "Abogados",
    claimWord: "firme",
    idea: "La quilla: la pieza que impide que el barco vuelque.",
  },
  {
    id: "meridian",
    domain: "meridianlegal.mx",
    email: "guillermo@meridianlegal.mx",
    name: "Meridian",
    suffix: "Legal",
    Mark: MeridianMark,
    tagline: "Abogados",
    claimWord: "trazada",
    idea: "El meridiano: una referencia que no se mueve nunca.",
  },
  {
    id: "helm",
    domain: "helmlegal.mx",
    email: "guillermo@helmlegal.mx",
    name: "Helm",
    suffix: "Legal",
    Mark: HelmMark,
    tagline: "Abogados",
    claimWord: "gobernada",
    idea: "El timón: alguien tiene que llevar el rumbo del asunto.",
  },
  {
    id: "vantage",
    domain: "vantagelegal.mx",
    email: "guillermo@vantagelegal.mx",
    name: "Vantage",
    suffix: "Legal",
    Mark: VantageMark,
    tagline: "Abogados",
    claimWord: "aventajada",
    idea: "El punto desde el que se ve más lejos que el resto.",
  },
  {
    id: "crest",
    domain: "crestlegal.mx",
    email: "guillermo@crestlegal.mx",
    name: "Crest",
    suffix: "Legal",
    Mark: CrestMark,
    tagline: "Abogados",
    claimWord: "coronada",
    idea: "La cresta de la ola: el punto más alto del movimiento.",
  },
];

const STORAGE_KEY = "gmr-brand";
const BrandContext = createContext(BRANDS[0]);

export const useBrand = () => useContext(BrandContext);

export function BrandProvider({ children }) {
  const [id, setId] = useState(() => {
    if (typeof window === "undefined") return BRANDS[0].id;
    // ?marca=norte gana sobre lo guardado, para poder mandar un link directo.
    const fromUrl = new URLSearchParams(window.location.search).get("marca");
    const saved = fromUrl || window.localStorage.getItem(STORAGE_KEY);
    return BRANDS.some((b) => b.id === saved) ? saved : BRANDS[0].id;
  });

  useEffect(() => {
    window.localStorage.setItem(STORAGE_KEY, id);
    document.title = `${brandById(id).name} Legal | Guillermo Morales Rodríguez`;
  }, [id]);

  const value = useMemo(() => ({ ...brandById(id), setBrand: setId }), [id]);

  return <BrandContext.Provider value={value}>{children}</BrandContext.Provider>;
}

function brandById(id) {
  return BRANDS.find((b) => b.id === id) ?? BRANDS[0];
}

/** Logo + nombre, tal como aparece en el navbar y el pie. */
export function BrandLogo({ size = "md", showTagline = true, reverse = false }) {
  const { name, suffix, tagline, Mark } = useBrand();
  const markCls = size === "sm" ? "w-5 h-5" : "w-7 h-7";
  const nameCls = size === "sm" ? "text-lg" : "text-xl";

  return (
    <span className="flex items-center gap-3">
      <Mark className={markCls} reverse={reverse} />
      <span className="leading-tight">
        <span className={`font-serif-display ${nameCls} font-semibold tracking-tight block`}>
          {name} <span className="font-light opacity-70">{suffix}</span>
        </span>
        {showTagline && (
          <span className="text-[10px] tracking-[0.25em] uppercase opacity-60">{tagline}</span>
        )}
      </span>
    </span>
  );
}
